import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, RadioGroup, Radio, Alert, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setMachineDetailsData } from '../../../redux/machineDetailsDataSlice';
import { CustomSingleSelect } from '../../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uploadIcon from '../../../images/upload.svg';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { UPLOAD_LIMITS, UPLOAD_LIMIT_LABELS } from '../../../utils/uploadLimits';
import '../../../css/machines/addMachineReportDialog.css';

export const AddReportDialoge = ({ fromMachinePage = false, openAddReport, setOpenAddReport, machine_id, machine_status, setTotalReports, setReports, setFilteredReportsCount }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const machineDetailsData = useSelector((state) => state.MachineDetailsData);

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState(null);

    const fileInputRef = useRef(null);

    const formik = useFormik({

        initialValues: {
            control_date: '',
            company: '',
            verifier: '',
            accompanist: '',
            status: machine_status,
            machine_id: machine_id || '',
        },

        validationSchema: Yup.object().shape({
            control_date: Yup.date().required(t('isRequired')),
            company: Yup.object().required(t('isRequired')),
            verifier: Yup.object().required(t('isRequired')),
            accompanist: Yup.object().required(t('isRequired')),
            status: Yup.string().required(t('isRequired')),
        }),

        onSubmit: async (values) => {
            try {
                setLoading(true);
                const formData = new FormData();

                // Append files
                documents.forEach((file) => {
                    formData.append('files', file.file);
                });

                // Append simple fields
                machine_id && formData.append('machine_id', values.machine_id || '');
                formData.append('control_date', new Date(values.control_date).toISOString());
                formData.append('company_id', values.company?.value || '');
                formData.append('verifier_id', values.verifier?.value || '');
                formData.append('accompanist_id', values.accompanist?.value || '');
                formData.append('status', values.status || '');


                // Send the FormData object via Axios
                const response = await axios.post('machine-control', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.status === 200 || response.status === 201) {
                    setReports(prevReports => [response.data, ...prevReports]);
                    setTotalReports(prevTotal => prevTotal + 1);
                    setFilteredReportsCount(prevCount => prevCount + 1);
                    if (fromMachinePage) {
                        dispatch(setSuccessfullModifyDialog({ open: true, type: 'Ajouter le rapport de la machine' }));
                        const isControlDateGreater = machineDetailsData.machine_control.every(
                            (control) => new Date(response.data.control_date) > new Date(control.control_date)
                        );

                        if (isControlDateGreater) {
                            dispatch(setMachineDetailsData({ ...machineDetailsData, status: response.data.status }));
                        }
                    }
                    setLoading(false);
                    setOpenAddReport(false);
                }
            } catch (error) {
                setLoading(false);
                console.error('Error adding report:', error);
                CustomErrorLogger(error, 'Error adding report in AddReportDialog');
                setErrMsg(error?.response?.data?.message || error.message || 'erreur lors de la création du rapport' );
            }
        }
    });

    const { handleSubmit, values, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenAddReport(false);
    }, [setOpenAddReport]);

    const onChangeCompany = useCallback((value) => {
        setFieldValue('company', value);
        setFieldValue('verifier', '');
    }, [setFieldValue]);

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company/findAllForReport', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                const companies = response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                    contacts: company.company_contact || [],
                }));

                // Automatically select the single option if only one is available
                if (companies.length === 1) {
                    onChangeCompany(companies[0]);
                }

                return companies;
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in AddReportDialoge");
        }
    }, [onChangeCompany]);

    useEffect(() => {
        if (values.company && values.company.contacts.length === 1) {
            const singleOption = values.company.contacts[0];
            setFieldValue('verifier', { value: singleOption.id, label: singleOption.name });
        }
    }, [values.company, setFieldValue]);

    const handleCreateCompanyContact = useCallback(async (newOption) => {
        try {
            const payload = {
                name: newOption.label,
                companyId: values.company.value,
            };
            const response = await axios.post('company-contact', payload);
            if (response.status === 200 || response.status === 201) {
                setFieldValue('verifier', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company-contact in AddReportDialoge:', error);
            CustomErrorLogger(error, "Error creating company-contact in AddReportDialoge");
        }
    }, [setFieldValue, values.company]);

    const fetchUsers = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('user', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.users.map(user => ({
                    value: user.id,
                    label: user.first_name + ' ' + user.last_name,
                }));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            CustomErrorLogger(error, "error fetching users in AddReportDialoge");
        }
    }, []);


    const handleUploadClick = () => fileInputRef.current.click();

    const handleUploadDocuments = (files) => {
        const newFiles = Array.from(files).flatMap((file) => {
            if (file.type !== 'application/pdf') {
                setErrMsg(t('Format PDF uniquement.'));
                return [];
            }
            if (file.size > UPLOAD_LIMITS.pdfDocumentBytes) {
                setErrMsg(t(`Format PDF de ${UPLOAD_LIMIT_LABELS.pdfDocument} maximum.`));
                return [];
            }
            return [{
                name: file?.name,
                size: (file?.size / (1024 * 1024))?.toFixed(2),
                file,
            }];
        });
        if (newFiles.length) {
            setErrMsg(null);
            setDocuments((prevDocuments) => [...prevDocuments, ...newFiles]);
        }
    };

    const handleRemoveDocument = (index) => {
        setDocuments(prevDocuments => prevDocuments.filter((_, i) => i !== index));
    };

    return (
        <Dialog open={openAddReport} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addReport_icon_button'>
                <Iconify icon="material-symbols:close" className='addReport_close_icon' />
            </IconButton>
            <DialogTitle className='addReport_dialog_title'>Ajouter un rapport</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addReport_dialog_content'>
                    <Box className='addReport_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Entreprise associée</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez une entreprise'
                            className='addReport_InputField'
                            required={true}
                            value={values.company}
                            fetchOnMount={true}
                            fetchFunction={fetchCompanies}
                            backendSearchable={true}
                            onChange={onChangeCompany}
                            helperText={touched.company && errors.company}
                            error={touched.company && Boolean(errors.company)}
                        />
                    </Box>
                    {values.company &&
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Vérificateur</FormHelperText>
                            <CustomSingleSelect
                                placeholder='Sélectionnez une Vérificateur'
                                className='addReport_InputField'
                                required={true}
                                value={values.verifier}
                                option={values.company.contacts.map(contact => ({ value: contact.id, label: contact.name }))}
                                backendSearchable={true}
                                onChange={(value) => setFieldValue('verifier', value)}
                                creatable={true}
                                createFunction={handleCreateCompanyContact}
                                helperText={touched.verifier && errors.verifier}
                                error={touched.verifier && Boolean(errors.verifier)}
                            />
                        </Box>
                    }
                    <Box className='addReport_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Accompagnateur</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez un accompagnateur'
                            className='addReport_InputField'
                            required={true}
                            value={values.accompanist}
                            fetchFunction={fetchUsers}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('accompanist', value)}
                            helperText={touched.accompanist && errors.accompanist}
                            error={touched.accompanist && Boolean(errors.accompanist)}
                        />
                    </Box>
                    <Box className="addReport_title_number_box">
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Date du contrôle</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addReport_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.control_date}
                                onChange={(event) => setFieldValue('control_date', event.target.value)}
                                onBlur={handleBlur}
                                name="control_date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.control_date && Boolean(errors.control_date)}
                                helperText={touched.control_date && errors.control_date}
                            />
                        </Box>
                    </Box>
                    <Box className="addReport_title_number_box">
                        <Box className='addReport_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Statut de la machine</FormHelperText>
                            <RadioGroup
                                name="contractAssociation"
                                className='addReport_radio_group'
                                value={values.status}
                                onChange={(event) => setFieldValue('status', event.target.value)}
                            >
                                <Box className='addReport_radio_box'>
                                    <Radio value='Valide' />
                                    <Typography className="addReport_radio_label">Valide</Typography>
                                </Box>
                                <Box className='addReport_radio_box'>
                                    <Radio value='NonConforme' />
                                    <Typography className="addReport_radio_label">Non conforme</Typography>
                                </Box>
                                <Box className='addReport_radio_box'>
                                    <Radio value='AvecReparation' />
                                    <Typography className="addReport_radio_label">Avec réparation</Typography>
                                </Box>
                            </RadioGroup>
                        </Box>
                    </Box>
                    <Box className='addReport_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addReportInputTitle">Document du rapport (optionnel)</FormHelperText>
                        <Box
                            sx={{ width: '100%' }}
                            onDragOver={(event) => event.preventDefault()} // Prevent default behavior to allow drop
                            onDrop={(event) => {
                                event.preventDefault();
                                handleUploadDocuments(event.dataTransfer.files); // Handle dropped files
                            }}
                        >
                            <Box className='modifierPhoto_upload_box' onClick={handleUploadClick}>
                                <img src={uploadIcon} alt="icon" className='modifierPhoto_upload_icons' loading='lazy' decoding='async' />
                                <Box>
                                    <Typography className='modifierPhoto_uplaod_first_typography'>Glisser-déposez le fichier ici, ou Parcourir</Typography>
                                    <Typography className='modifierPhoto_uplaod_second_typography'>{t(`Format PDF de ${UPLOAD_LIMIT_LABELS.pdfDocument} maximum`)}</Typography>
                                </Box>
                            </Box>
                            <input
                                type="file"
                                accept=".pdf"
                                ref={fileInputRef}
                                multiple
                                style={{ display: "none" }}
                                onChange={event => handleUploadDocuments(event.target.files)}
                            />
                        </Box>
                    </Box>
                    <Box className='addReport_documents_box'>
                        {documents.map((doc, index) => (
                            <Box
                                className='addReport_document_item'
                                key={index}
                            >
                                <Iconify className='addReport_pdf_icon' icon="bxs:file-pdf" />
                                <Typography className='addReport_doc_name'>{doc.name}</Typography>
                                <Typography className='addReport_doc_size'>{doc.size} MB</Typography>
                                <Iconify className='addReport_delete_icon' icon="ic:outline-delete" onClick={() => handleRemoveDocument(index)} />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions className='addReport_dialog_actions'>
                    <Button className='addReport_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <LoadingButton loading={loading} className='addReport_button_valider' variant="contained" type="submit">{t('Valider')}</LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};